import { motion } from "framer-motion";
import { ReactNode } from "react";

interface AnimatedTextProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "div" | "ol" | "ul" | "blockquote";
  animation?: {
    initial?: any;
    animate?: any;
    transition?: any;
  };
}

const AnimatedText = ({
  children,
  className = "",
  delay = 0,
  duration = 0.6,
  as = "p",
  animation
}: AnimatedTextProps) => {
  const defaultAnimation = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration, delay }
  };

  const finalAnimation = animation || defaultAnimation;

  const MotionComponent = motion[as];

  return (
    <MotionComponent
      initial={finalAnimation.initial}
      animate={finalAnimation.animate}
      transition={finalAnimation.transition}
      className={className}
    >
      {children}
    </MotionComponent>
  );
};

// Specific components for common use cases
export const AnimatedHeading = ({
  children,
  className = "",
  delay = 0,
  as = "h2",
  ...props
}: Omit<AnimatedTextProps, "as"> & { as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" }) => (
  <AnimatedText
    as={as}
    className={`font-bold text-white ${className}`}
    delay={delay}
    {...props}
  >
    {children}
  </AnimatedText>
);

export const AnimatedParagraph = ({
  children,
  className = "",
  delay = 0,
  ...props
}: Omit<AnimatedTextProps, "as">) => (
  <AnimatedText
    as="p"
    className={`text-slate-300 leading-relaxed ${className}`}
    delay={delay}
    {...props}
  >
    {children}
  </AnimatedText>
);

export const AnimatedList = ({
  children,
  className = "",
  delay = 0,
  ordered = false,
  ...props
}: Omit<AnimatedTextProps, "as"> & { ordered?: boolean }) => (
  <AnimatedText
    as={ordered ? "ol" : "ul"}
    className={`${ordered ? "list-decimal" : "list-disc"} list-inside text-slate-300 space-y-2 ${className}`}
    delay={delay}
    {...props}
  >
    {children}
  </AnimatedText>
);

export const AnimatedBlockquote = ({
  children,
  className = "",
  delay = 0,
  ...props
}: Omit<AnimatedTextProps, "as">) => (
  <AnimatedText
    as="blockquote"
    className={`border-l-4 border-teal-400 pl-4 my-6 italic text-slate-300 ${className}`}
    delay={delay}
    {...props}
  >
    {children}
  </AnimatedText>
);

export default AnimatedText;
